import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { DocumentTitle } from '@/components/DocumentTitle';
import { useModalManagerStore } from '@/components/Modal';
import { Navbar } from '@/components/Navbar/Navbar';
import { eStrings } from '@/constants/strings';
import { Message } from '@/modals/Message';

import { SearchFilter } from './-index/components/SearchFilter';
import { SearchInput } from './-index/components/SearchInput';
import { SearchBedrijfByLabelView } from './-index/views/SearchBedrijfByLabelView';
import {
  SearchBedrijfView,
  preloadSearchBedrijfView,
} from './-index/views/SearchBedrijfView';

const searchSchema = z.object({
  query: z.string().optional(),
  straat: z.string().optional(),
  postcode: z.string().or(z.number()).transform(String).optional(),
  plaats: z.string().optional(),
  locatienummer: z.string().optional(),
  p: z.number().optional().default(1),
  error: z.enum(['uitgelogd']).optional().catch(undefined),
  labels: z.array(z.string()).optional().catch([]),
});

export type SearchParams = z.infer<typeof searchSchema>;

/**
 * Route
 */
export const Route = createFileRoute('/')({
  validateSearch: searchSchema.parse,
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context: { queryClient }, deps }) => {
    if (!deps.search.labels?.length) {
      await preloadSearchBedrijfView({
        queryClient,
        search: deps.search,
      });
    }
  },
  onEnter: ({ search }) => {
    if (search.error) {
      const openModal = useModalManagerStore.getState().openModal;

      switch (search.error) {
        case 'uitgelogd':
          openModal(Message, {
            title: 'Je bent uitgelogd',
            description:
              'Vanwege inactiviteit en uit veiligheidsoverwegingen ben je automatisch uitgelogd.',
          });
          break;
        default:
          break;
      }
    }
  },
  component: IndexComponent,
});

/**
 * Route component
 */
function IndexComponent() {
  const search = Route.useSearch();

  return (
    <>
      <DocumentTitle sections={[eStrings.LBL_DIGI_V, 'Zoeken']} />

      <Navbar />

      <div className="container-default mt-2">
        <SearchInput />
      </div>

      <SearchFilter />

      {search.labels?.length ? (
        <SearchBedrijfByLabelView />
      ) : (
        <SearchBedrijfView />
      )}
    </>
  );
}
